/**
 * web-client-app D6 — the website's reads.
 *
 * A guest reads the studio's public catalog under `/api/site/*` (no session,
 * cacheable). The catalog pieces ask `useCatalogSource()` which source to read;
 * the guest source reads `/api/site/*`; once signed in (web-sign-in-checkout)
 * the client source reads the same shapes from `/api/site/client/*`. The
 * source is part of every query key, so a sign-out drops every signed-in query
 * at once (`['web', 'client', ...]`, session.ts).
 *
 * Own small fetch, not the Mini App's `apiFetch`: that one is bound to
 * `/api/webapp` and its Telegram session recovery.
 */
import { keepPreviousData, useInfiniteQuery, useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  SiteAvailableDates,
  SiteDayTimes,
  SiteEventDto,
  SiteEventsPage,
  SiteHappeningSoon,
  SiteNextOpenings,
  SiteOpenTimes,
  SiteSeriesDto,
  SiteServiceDto,
  SiteServiceSummaryDto,
  SiteSoonestOfferings,
  SiteStudioDto,
} from '@telify/shared';

import { readBoot } from '../boot';
import { siteClientFetch, useWebSession } from './session';

export class SiteFetchError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
    /** The error's whole answer, for the codes that carry more (ACTIVE_FLOW_EXISTS names the open flow). */
    readonly body: Readonly<Record<string, unknown>> = {},
  ) {
    super(message);
    this.name = 'SiteFetchError';
  }
}

export async function siteFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/site${path}`, { ...init, headers: { Accept: 'application/json', ...init?.headers } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new SiteFetchError(res.status, body.error, body.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * Which catalog a piece reads: the guest's public one, or, once signed in, the
 * client's own (web-sign-in-checkout D5: `/api/site/client/*`, the same shapes
 * read for the signed-in client, so a tag-unlocked offering shows).
 */
export type CatalogSource = 'guest' | 'client';

export function useCatalogSource(): CatalogSource {
  return useWebSession().status === 'signedIn' ? 'client' : 'guest';
}

/** A catalog read from the source's own address. */
function catalogFetch<T>(src: CatalogSource, path: string): Promise<T> {
  return src === 'client' ? siteClientFetch<T>(path) : siteFetch<T>(path);
}

export const siteKeys = {
  all: ['web'] as const,
  studio: () => ['web', 'studio'] as const,
  services: (src: CatalogSource) => ['web', src, 'services'] as const,
  popular: () => ['web', 'popular'] as const,
  service: (src: CatalogSource, id: string) => ['web', src, 'service', id] as const,
  events: (src: CatalogSource) => ['web', src, 'events'] as const,
  event: (src: CatalogSource, id: string) => ['web', src, 'event', id] as const,
  series: (src: CatalogSource, id: string) => ['web', src, 'series', id] as const,
  happeningSoon: () => ['web', 'happening-soon'] as const,
  availableDates: (src: CatalogSource, id: string, from: string, to: string, tz: string) =>
    ['web', src, 'dates', id, from, to, tz] as const,
  dayTimes: (src: CatalogSource, id: string, date: string, tz: string) => ['web', src, 'times', id, date, tz] as const,
  nextOpenings: (src: CatalogSource, id: string, tz: string) => ['web', src, 'openings', id, tz] as const,
  soonestOfferings: (limit: number) => ['web', 'offerings', limit] as const,
  openTimes: () => ['web', 'open-times'] as const,
};

/** A missing or non-public offering is a 404: never retried. */
const retryUnlessNotFound = (count: number, err: unknown) =>
  !(err instanceof SiteFetchError && err.status < 500) && count < 2;

export function useSiteStudio() {
  const boot = readBoot();
  return useQuery({
    queryKey: siteKeys.studio(),
    queryFn: () => siteFetch<SiteStudioDto>('/studio'),
    initialData: boot?.studio,
    staleTime: 60_000,
  });
}

export function useSiteServices({ enabled = true }: { enabled?: boolean } = {}) {
  const src = useCatalogSource();
  return useQuery({
    queryKey: siteKeys.services(src),
    queryFn: async () => (await catalogFetch<{ services: SiteServiceSummaryDto[] }>(src, '/services')).services,
    enabled,
    staleTime: 30_000,
  });
}

/** The listed services' ids by confirmed bookings in the last 90 days, most first; one with none is left out. */
export function usePopularServiceIds() {
  return useQuery({
    queryKey: siteKeys.popular(),
    queryFn: async () => (await siteFetch<{ serviceIds: string[] }>('/services/popular')).serviceIds,
    staleTime: 60_000,
  });
}

/** The services with the most booked first; the rest keep the studio order after them. */
export function mostBookedFirst<T extends { id: string }>(services: readonly T[], rankedIds: readonly string[]): T[] {
  const rank = new Map(rankedIds.map((id, i) => [id, i]));
  return services
    .map((s, i) => ({ s, key: rank.get(s.id) ?? rankedIds.length + i }))
    .sort((a, b) => a.key - b.key)
    .map((x) => x.s);
}

export function useSiteService(id: string | undefined) {
  const src = useCatalogSource();
  return useQuery({
    queryKey: siteKeys.service(src, id ?? ''),
    queryFn: async () => (await catalogFetch<{ service: SiteServiceDto }>(src, `/services/${encodeURIComponent(id!)}`)).service,
    enabled: Boolean(id),
    retry: retryUnlessNotFound,
    staleTime: 30_000,
  });
}

export function useSiteEvents({ enabled = true }: { enabled?: boolean } = {}) {
  const src = useCatalogSource();
  return useInfiniteQuery({
    queryKey: siteKeys.events(src),
    queryFn: ({ pageParam }) =>
      catalogFetch<SiteEventsPage>(src, pageParam ? `/events?cursor=${encodeURIComponent(pageParam)}` : '/events'),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    staleTime: 30_000,
  });
}

export function useSiteEvent(id: string | undefined) {
  const src = useCatalogSource();
  return useQuery({
    queryKey: siteKeys.event(src, id ?? ''),
    queryFn: async () => (await catalogFetch<{ event: SiteEventDto }>(src, `/events/${encodeURIComponent(id!)}`)).event,
    enabled: Boolean(id),
    retry: retryUnlessNotFound,
    staleTime: 30_000,
  });
}

/** shared-client-screens D5: a series' page, the guest's or the signed-in client's (its `userBooking`). */
export function useSiteSeries(id: string | undefined) {
  const src = useCatalogSource();
  return useQuery({
    queryKey: siteKeys.series(src, id ?? ''),
    queryFn: async () => (await catalogFetch<{ series: SiteSeriesDto }>(src, `/series/${encodeURIComponent(id!)}`)).series,
    enabled: Boolean(id),
    retry: retryUnlessNotFound,
    staleTime: 30_000,
  });
}

/** shared-client-screens H7: the guest home's Happening Soon, events and series as the app's Home lists them. */
export function useSiteHappeningSoon() {
  return useQuery({
    queryKey: siteKeys.happeningSoon(),
    queryFn: async () => (await siteFetch<SiteHappeningSoon>('/happening-soon')).items,
    staleTime: 30_000,
  });
}

export function useAvailableDates(serviceId: string | undefined, from: string, to: string, tz: string) {
  const src = useCatalogSource();
  return useQuery({
    queryKey: siteKeys.availableDates(src, serviceId ?? '', from, to, tz),
    queryFn: () =>
      catalogFetch<SiteAvailableDates>(
        src,
        `/services/${encodeURIComponent(serviceId!)}/available-dates?from=${from}&to=${to}&tz=${encodeURIComponent(tz)}`,
      ),
    enabled: Boolean(serviceId),
    placeholderData: keepPreviousData,
    retry: retryUnlessNotFound,
  });
}

export function useDayTimes(serviceId: string | undefined, date: string | null, tz: string) {
  const src = useCatalogSource();
  return useQuery({
    queryKey: siteKeys.dayTimes(src, serviceId ?? '', date ?? '', tz),
    queryFn: () =>
      catalogFetch<SiteDayTimes>(
        src,
        `/services/${encodeURIComponent(serviceId!)}/availability?date=${date}&tz=${encodeURIComponent(tz)}`,
      ),
    enabled: Boolean(serviceId && date),
    // The last day's times stay while a newly picked day's load (its `date` says which day they are), so a
    // phone page keeps its height instead of dropping to a loading line (Gabriel 2026-10-21).
    placeholderData: keepPreviousData,
    retry: retryUnlessNotFound,
  });
}

/** One service's next-openings read: the one key, fetch and retry rule behind both hooks below. */
function nextOpeningsQuery(src: CatalogSource, serviceId: string | undefined, tz: string) {
  return {
    queryKey: siteKeys.nextOpenings(src, serviceId ?? '', tz),
    queryFn: () =>
      catalogFetch<SiteNextOpenings>(
        src,
        `/next-openings?serviceId=${encodeURIComponent(serviceId!)}&tz=${encodeURIComponent(tz)}`,
      ),
    enabled: Boolean(serviceId),
    retry: retryUnlessNotFound,
  };
}

export function useNextOpenings(serviceId: string | undefined, tz: string) {
  const src = useCatalogSource();
  return useQuery(nextOpeningsQuery(src, serviceId, tz));
}

/** What a many-service read keeps of each answer. Outside the hook, so its result keeps its identity until an answer changes. */
const openingsOf = (results: UseQueryResult<SiteNextOpenings>[]) =>
  results.map(({ data, isError, isPending }) => ({ data, isError, isPending }));

/** web-studio-sites D15: several services' next openings at once, in the order given (services with their times). */
export function useNextOpeningsFor(serviceIds: readonly string[], tz: string) {
  const src = useCatalogSource();
  return useQueries({ queries: serviceIds.map((id) => nextOpeningsQuery(src, id, tz)), combine: openingsOf });
}

/**
 * web-studio-sites D15: the soonest of everything the studio offers, soonest
 * first (the guest's read; it has no signed-in twin). Instants only, the same
 * for every visitor; the piece words each one in the visitor's zone.
 */
export function useSoonestOfferings(limit: number) {
  return useQuery({
    queryKey: siteKeys.soonestOfferings(limit),
    queryFn: async () => (await siteFetch<SiteSoonestOfferings>(`/offerings/soonest?limit=${limit}`)).offerings,
    staleTime: 30_000,
  });
}

/**
 * web-studio-sites D15: every listed service's open times in one read, the
 * studio's shared answer (the guest's catalog). Instants only, the same for
 * every visitor; the services piece groups them into the visitor's own days.
 */
export function useOpenTimes(enabled: boolean) {
  return useQuery({
    queryKey: siteKeys.openTimes(),
    queryFn: async () => (await siteFetch<SiteOpenTimes>('/services/open-times')).services,
    enabled,
    staleTime: 30_000,
  });
}
